import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../reducers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import {
  LIBRARY_VIEW_ACTIONS,
  ActionType,
} from '../../selectedVideo/actions/actions';
import { downloadFile } from '../../selectedVideo/selectedVideoSlice';
import { pushModal } from '../../globalModals/globalModalsSlice';
import type { FileType, requestStatusType } from '../../../types';
import ActionMenuItem from '../../plan/upgrade/ActionMenuItem';
import { isDemoVideo } from '../../../constants/demoVideo';
import { ShareDialog } from '../../../components/shared/ShareDialog';

interface FileActionMenuProps {
  file: FileType;
  onOpenChange?: (open: boolean) => void;
  onRename?: () => void;
}

const FileActionMenu = ({
  file,
  onOpenChange,
  onRename,
}: FileActionMenuProps) => {
  const [open, setOpen] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionType | null>(null);
  const [actionStatus, setActionStatus] = useState<requestStatusType>('idle');
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Get current user ID to determine if file is a shared private file
  const currentUserId = useSelector(
    (state: RootState) => state.settings.user?._id,
  );

  // A shared private file is one that:
  // 1. Has no parent folder (parentId is null) - it's in someone's private folder
  // 2. Is not owned by the current user
  const isSharedPrivateFile = !file.parentId && file.ownerId !== currentUserId;

  const handleMenuClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleAction = React.useCallback(
    async (key: ActionType) => {
      const { _id: fileId, defaultProfile } = file;
      setSelectedAction(key);
      setActionStatus('pending');
      switch (key) {
        case 'share':
          setShowShareDialog(true);
          break;
        case 'download':
          if (defaultProfile) {
            await dispatch(downloadFile({ fileId, profile: defaultProfile }));
          }
          break;
        case 'delete':
          await dispatch(
            pushModal({ type: 'delete-files-modal', files: [file] }),
          );
          break;
        case 'move':
          await dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
          break;
        case 'rename':
          if (onRename) {
            onRename();
          } else {
            await dispatch(
              pushModal({ type: 'rename-file-modal', files: [file] }),
            );
          }
          break;
        default:
      }
      setActionStatus('idle');
      handleMenuClose();
    },
    [dispatch, file, handleMenuClose],
  );

  const menuItems = React.useMemo(() => {
    // Filter actions based on whether this is a shared private file
    // For shared private files (in someone else's private folder), only allow: download
    const allowedActions = isSharedPrivateFile
      ? LIBRARY_VIEW_ACTIONS.filter(
          ({ actionType }) => actionType === 'download',
        )
      : LIBRARY_VIEW_ACTIONS;

    const items = allowedActions.map(
      ({ actionType, tooltipMessages, buttonTexts, IconComponent }) => {
        const isSelectedAction = selectedAction === actionType;
        const isLoading = isSelectedAction && actionStatus === 'pending';
        const status = isSelectedAction ? actionStatus : 'idle';
        const isDestructive = actionType === 'delete';

        return {
          actionType,
          element: (
            <ActionMenuItem
              key={actionType}
              onClick={() => handleAction(actionType)}
              tooltipMessage={t(tooltipMessages[status])}
              loading={isLoading}
              startIcon={<IconComponent className="h-4 w-4" />}
              destructive={isDestructive}
            >
              {t(buttonTexts[status])}
            </ActionMenuItem>
          ),
        };
      },
    );

    // Add separator before delete action
    const result: React.ReactNode[] = [];
    items.forEach((item, index) => {
      if (item.actionType === 'delete' && index > 0) {
        result.push(<DropdownMenuSeparator key="separator-delete" />);
      }
      result.push(item.element);
    });

    return result;
  }, [selectedAction, actionStatus, handleAction, t, isSharedPrivateFile]);

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  // Hide action menu for demo video
  if (isDemoVideo(file._id)) {
    return null;
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="more-actions-button"
            variant="ghost"
            size="sm"
            className="w-7 h-7 p-0 rounded-md hover:bg-accent/50 transition-colors duration-150"
          >
            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="w-48 p-1 bg-popover/95 backdrop-blur-xl border border-border/50 shadow-lg"
        >
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu>
      <ShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        file={file}
      />
    </>
  );
};

export default FileActionMenu;
